import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, getDay, addMonths, subMonths } from 'date-fns';
import { tr } from 'date-fns/locale';

const token = () => localStorage.getItem('crm_token');
const api = (path, opts = {}) => axios({ url: `/api/wedding-reservations${path}`, headers: { Authorization: `Bearer ${token()}` }, ...opts });

const STATUS = {
  pending:    { label: 'Bekliyor',    bg: 'rgba(217,119,6,0.15)',  color: '#FCD34D' },
  confirmed:  { label: 'Onaylandı',  bg: 'rgba(5,150,105,0.15)',  color: '#34D399' },
  in_progress:{ label: 'Devam Ediyor',bg:'rgba(37,99,235,0.15)',  color: '#60A5FA' },
  completed:  { label: 'Tamamlandı', bg: 'rgba(107,114,128,0.15)',color: '#9CA3AF' },
  cancelled:  { label: 'İptal',      bg: 'rgba(220,38,38,0.15)',  color: '#FCA5A5' },
};

export default function WeddingReservations() {
  const [reservations, setReservations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar'); // 'calendar' | 'list'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [selectedRes, setSelectedRes] = useState(null);
  const [form, setForm] = useState({
    customer_id: '', package_id: '', event_date: '', start_time: '18:00',
    end_time: '24:00', guest_count: '', hall_name: '', total_price: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const fetchData = useCallback(async () => {
    const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
    const end   = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
    const [resData, custData, pkgData] = await Promise.all([
      api(`?start_date=${start}&end_date=${end}${filterStatus ? '&status=' + filterStatus : ''}`),
      axios.get('/api/customers?limit=500', { headers: { Authorization: `Bearer ${token()}` } }),
      axios.get('/api/wedding-packages', { headers: { Authorization: `Bearer ${token()}` } }),
    ]);
    setReservations(resData.data);
    setCustomers(custData.data.customers || custData.data.data || []);
    setPackages(pkgData.data);
    setLoading(false);
  }, [currentMonth, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async () => {
    if (!form.customer_id || !form.event_date || !form.start_time || !form.end_time) return;
    setSaving(true);
    try {
      await api('/', { method: 'post', data: form });
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (e) {
      alert(e.response?.data?.error || 'Hata oluştu');
    } finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    await api(`/${id}`, { method: 'put', data: { status } });
    fetchData();
  };

  const resetForm = () => setForm({ customer_id:'', package_id:'', event_date:'', start_time:'18:00', end_time:'24:00', guest_count:'', hall_name:'', total_price:'', notes:'' });

  // Takvim hesaplama
  const days = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });
  const startPad = getDay(days[0]); // 0=Pazar

  const getResForDay = (day) => reservations.filter(r => isSameDay(parseISO(r.event_date), day));

  if (loading) return <div className="wh-loading"><div className="wh-spinner" /><span>Yükleniyor...</span></div>;

  return (
    <div className="wh-page">
      {/* Header */}
      <div className="wh-header">
        <div>
          <h1 className="wh-title">💒 Salon Rezervasyonları</h1>
          <p className="wh-subtitle">Düğün ve etkinlik takvimi</p>
        </div>
        <div className="wh-header-actions">
          <select className="wh-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Tüm Durumlar</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="wh-view-toggle">
            <button className={`wh-toggle-btn ${view === 'calendar' ? 'active' : ''}`} onClick={() => setView('calendar')}>Takvim</button>
            <button className={`wh-toggle-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>Liste</button>
          </div>
          <button className="wh-btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>+ Yeni Rezervasyon</button>
        </div>
      </div>

      {/* Takvim */}
      {view === 'calendar' && (
        <div className="wh-card">
          <div className="wh-cal-nav">
            <button className="wh-nav-btn" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>‹</button>
            <span className="wh-cal-title">{format(currentMonth, 'MMMM yyyy', { locale: tr })}</span>
            <button className="wh-nav-btn" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>›</button>
          </div>
          <div className="wh-cal-grid">
            {['Paz','Pzt','Sal','Çar','Per','Cum','Cmt'].map(d => <div key={d} className="wh-cal-dow">{d}</div>)}
            {Array(startPad).fill(null).map((_, i) => <div key={`pad-${i}`} className="wh-cal-cell empty" />)}
            {days.map(day => {
              const dayRes = getResForDay(day);
              const isToday = isSameDay(day, new Date());
              return (
                <div key={day.toISOString()} className={`wh-cal-cell ${isToday ? 'today' : ''} ${dayRes.length > 0 ? 'has-event' : ''}`}>
                  <div className="wh-cal-day">{format(day, 'd')}</div>
                  {dayRes.map(r => (
                    <div key={r.id} className="wh-cal-event"
                      style={{ background: STATUS[r.status]?.bg, color: STATUS[r.status]?.color }}
                      onClick={() => setSelectedRes(r)}>
                      {r.start_time?.slice(0,5)} {r.customer_first_name}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Liste */}
      {view === 'list' && (
        <div className="wh-card">
          <table className="wh-table">
            <thead>
              <tr>
                <th>Tarih</th><th>Müşteri</th><th>Saat</th><th>Misafir</th><th>Paket</th><th>Tutar</th><th>Durum</th><th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {reservations.length === 0 && (
                <tr><td colSpan={8} className="wh-empty">Bu ayda rezervasyon yok</td></tr>
              )}
              {reservations.map(r => (
                <tr key={r.id} className="wh-tr" onClick={() => setSelectedRes(r)}>
                  <td><strong>{format(parseISO(r.event_date), 'dd MMM yyyy', { locale: tr })}</strong></td>
                  <td>{r.customer_first_name} {r.customer_last_name}<br/><small>{r.customer_phone}</small></td>
                  <td>{r.start_time?.slice(0,5)} – {r.end_time?.slice(0,5)}</td>
                  <td>{r.guest_count} kişi</td>
                  <td>{r.package_name || '—'}</td>
                  <td>{Number(r.total_price).toLocaleString('tr-TR')} ₺</td>
                  <td><span className="wh-badge" style={{ background: STATUS[r.status]?.bg, color: STATUS[r.status]?.color }}>{STATUS[r.status]?.label}</span></td>
                  <td>
                    <select className="wh-select-sm" value={r.status} onChange={e => { e.stopPropagation(); updateStatus(r.id, e.target.value); }} onClick={e => e.stopPropagation()}>
                      {Object.entries(STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Yeni Rezervasyon Modal */}
      {showModal && (
        <div className="wh-overlay" onClick={() => setShowModal(false)}>
          <div className="wh-modal" onClick={e => e.stopPropagation()}>
            <div className="wh-modal-header">
              <h2>Yeni Rezervasyon</h2>
              <button onClick={() => setShowModal(false)} className="wh-close">✕</button>
            </div>
            <div className="wh-modal-body">
              <div className="wh-form-grid">
                <div className="wh-field">
                  <label>Müşteri *</label>
                  <select value={form.customer_id} onChange={e => setForm({...form, customer_id: e.target.value})}>
                    <option value="">Seçin...</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                  </select>
                </div>
                <div className="wh-field">
                  <label>Paket</label>
                  <select value={form.package_id} onChange={e => setForm({...form, package_id: e.target.value})}>
                    <option value="">Paketsiz</option>
                    {packages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="wh-field">
                  <label>Etkinlik Tarihi *</label>
                  <input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} />
                </div>
                <div className="wh-field">
                  <label>Salon</label>
                  <input type="text" placeholder="Salon adı" value={form.hall_name} onChange={e => setForm({...form, hall_name: e.target.value})} />
                </div>
                <div className="wh-field">
                  <label>Başlangıç Saati *</label>
                  <input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} />
                </div>
                <div className="wh-field">
                  <label>Bitiş Saati *</label>
                  <input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} />
                </div>
                <div className="wh-field">
                  <label>Misafir Sayısı</label>
                  <input type="number" placeholder="0" value={form.guest_count} onChange={e => setForm({...form, guest_count: e.target.value})} />
                </div>
                <div className="wh-field">
                  <label>Toplam Tutar (₺)</label>
                  <input type="number" placeholder="0.00" value={form.total_price} onChange={e => setForm({...form, total_price: e.target.value})} />
                </div>
                <div className="wh-field wh-field-full">
                  <label>Notlar</label>
                  <textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
                </div>
              </div>
            </div>
            <div className="wh-modal-footer">
              <button className="wh-btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
              <button className="wh-btn-primary" onClick={save} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Detay Modal */}
      {selectedRes && (
        <div className="wh-overlay" onClick={() => setSelectedRes(null)}>
          <div className="wh-modal" onClick={e => e.stopPropagation()}>
            <div className="wh-modal-header">
              <h2>Rezervasyon Detayı</h2>
              <button onClick={() => setSelectedRes(null)} className="wh-close">✕</button>
            </div>
            <div className="wh-modal-body">
              <div className="wh-detail-grid">
                <div className="wh-detail-item"><span>Müşteri</span><strong>{selectedRes.customer_first_name} {selectedRes.customer_last_name}</strong></div>
                <div className="wh-detail-item"><span>Tarih</span><strong>{format(parseISO(selectedRes.event_date), 'dd MMMM yyyy', { locale: tr })}</strong></div>
                <div className="wh-detail-item"><span>Saat</span><strong>{selectedRes.start_time?.slice(0,5)} – {selectedRes.end_time?.slice(0,5)}</strong></div>
                <div className="wh-detail-item"><span>Misafir</span><strong>{selectedRes.guest_count} kişi</strong></div>
                <div className="wh-detail-item"><span>Paket</span><strong>{selectedRes.package_name || '—'}</strong></div>
                <div className="wh-detail-item"><span>Toplam Tutar</span><strong>{Number(selectedRes.total_price).toLocaleString('tr-TR')} ₺</strong></div>
                <div className="wh-detail-item"><span>Ödenen</span><strong>{Number(selectedRes.paid_amount).toLocaleString('tr-TR')} ₺</strong></div>
                <div className="wh-detail-item"><span>Durum</span><span className="wh-badge" style={{ background: STATUS[selectedRes.status]?.bg, color: STATUS[selectedRes.status]?.color }}>{STATUS[selectedRes.status]?.label}</span></div>
                {selectedRes.notes && <div className="wh-detail-item wh-detail-full"><span>Notlar</span><p>{selectedRes.notes}</p></div>}
              </div>
            </div>
            <div className="wh-modal-footer">
              <button className="wh-btn-secondary" onClick={() => setSelectedRes(null)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .wh-loading { display:flex; align-items:center; justify-content:center; gap:12px; height:300px; color:#9CA3AF; font-size:16px; }
        .wh-spinner { width:32px; height:32px; border:3px solid rgba(255,255,255,0.08); border-top-color:#3B82F6; border-radius:50%; animation:spin 0.8s linear infinite; }
        @keyframes spin { to { transform:rotate(360deg); } }
        .wh-page { padding:24px; max-width:1400px; }
        .wh-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; flex-wrap:wrap; gap:12px; }
        .wh-title { font-size:24px; font-weight:700; color:#FFFFFF; margin:0; }
        .wh-subtitle { font-size:14px; color:#9CA3AF; margin:4px 0 0; }
        .wh-header-actions { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
        .wh-btn-primary { background:linear-gradient(135deg,#3B82F6,#2563EB); color:#fff; border:none; padding:10px 20px; border-radius:10px; font-weight:600; cursor:pointer; font-size:14px; transition:all 0.2s; box-shadow:0 4px 12px rgba(59,130,246,0.3); }
        .wh-btn-primary:hover { transform:translateY(-1px); box-shadow:0 6px 16px rgba(59,130,246,0.4); }
        .wh-btn-primary:disabled { opacity:0.6; cursor:not-allowed; transform:none; }
        .wh-btn-secondary { background:rgba(255,255,255,0.06); color:#E5E7EB; border:1px solid rgba(255,255,255,0.1); padding:10px 20px; border-radius:10px; font-weight:600; cursor:pointer; font-size:14px; }
        .wh-select { padding:8px 12px; border:1px solid rgba(255,255,255,0.08); border-radius:8px; font-size:14px; background:rgba(26,26,36,0.8); color:#E5E7EB; cursor:pointer; }
        .wh-view-toggle { display:flex; background:rgba(26,26,36,0.6); border-radius:8px; overflow:hidden; border:1px solid rgba(255,255,255,0.06); }
        .wh-toggle-btn { padding:8px 16px; border:none; background:transparent; cursor:pointer; font-size:14px; font-weight:500; color:#9CA3AF; transition:all 0.2s; }
        .wh-toggle-btn.active { background:rgba(59,130,246,0.15); color:#60A5FA; border-radius:8px; }
        .wh-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.06); border-radius:16px; overflow:hidden; backdrop-filter:blur(12px); }
        .wh-cal-nav { display:flex; align-items:center; justify-content:space-between; padding:20px 24px; border-bottom:1px solid rgba(255,255,255,0.05); }
        .wh-cal-title { font-size:18px; font-weight:700; color:#FFFFFF; text-transform:capitalize; }
        .wh-nav-btn { background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:8px; width:36px; height:36px; cursor:pointer; font-size:20px; color:#9CA3AF; display:flex; align-items:center; justify-content:center; }
        .wh-cal-grid { display:grid; grid-template-columns:repeat(7,1fr); }
        .wh-cal-dow { text-align:center; padding:10px; font-size:12px; font-weight:600; color:#6B7280; border-bottom:1px solid rgba(255,255,255,0.05); }
        .wh-cal-cell { min-height:90px; padding:8px; border-right:1px solid rgba(255,255,255,0.03); border-bottom:1px solid rgba(255,255,255,0.03); cursor:default; }
        .wh-cal-cell.empty { background:rgba(0,0,0,0.2); }
        .wh-cal-cell.today .wh-cal-day { background:#3B82F6; color:#fff; border-radius:50%; width:26px; height:26px; display:flex; align-items:center; justify-content:center; box-shadow:0 0 8px rgba(59,130,246,0.5); }
        .wh-cal-cell.has-event { background:rgba(59,130,246,0.05); }
        .wh-cal-day { font-size:13px; font-weight:600; color:#E5E7EB; margin-bottom:4px; width:26px; height:26px; display:flex; align-items:center; justify-content:center; }
        .wh-cal-event { font-size:11px; font-weight:500; padding:3px 6px; border-radius:6px; margin-bottom:3px; cursor:pointer; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .wh-table { width:100%; border-collapse:collapse; font-size:14px; }
        .wh-table th { padding:12px 16px; text-align:left; font-weight:600; color:#6B7280; background:rgba(26,26,36,0.6); font-size:12px; text-transform:uppercase; letter-spacing:0.5px; }
        .wh-table td { padding:14px 16px; border-bottom:1px solid rgba(255,255,255,0.04); color:#E5E7EB; vertical-align:middle; }
        .wh-tr { transition:background 0.15s; cursor:pointer; }
        .wh-tr:hover { background:rgba(59,130,246,0.05); }
        .wh-empty { text-align:center; padding:40px; color:#6B7280; }
        .wh-badge { display:inline-block; padding:4px 10px; border-radius:20px; font-size:12px; font-weight:600; }
        .wh-select-sm { padding:4px 8px; border:1px solid rgba(255,255,255,0.08); border-radius:6px; font-size:12px; background:rgba(26,26,36,0.8); color:#E5E7EB; }
        .wh-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.75); backdrop-filter:blur(8px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px; }
        .wh-modal { background:rgba(17,17,24,0.95); border:1px solid rgba(255,255,255,0.1); border-radius:20px; width:100%; max-width:640px; max-height:90vh; display:flex; flex-direction:column; box-shadow:0 24px 48px rgba(0,0,0,0.6); backdrop-filter:blur(40px); }
        .wh-modal-header { display:flex; align-items:center; justify-content:space-between; padding:20px 24px; border-bottom:1px solid rgba(255,255,255,0.06); }
        .wh-modal-header h2 { margin:0; font-size:18px; font-weight:700; color:#FFFFFF; }
        .wh-close { background:rgba(255,255,255,0.06); border:none; font-size:18px; color:#9CA3AF; cursor:pointer; width:32px; height:32px; display:flex; align-items:center; justify-content:center; border-radius:8px; }
        .wh-close:hover { background:rgba(255,255,255,0.1); color:#FFFFFF; }
        .wh-modal-body { padding:24px; overflow-y:auto; flex:1; }
        .wh-modal-footer { display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid rgba(255,255,255,0.06); }
        .wh-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
        .wh-field { display:flex; flex-direction:column; gap:6px; }
        .wh-field-full { grid-column:1/-1; }
        .wh-field label { font-size:13px; font-weight:600; color:#E5E7EB; }
        .wh-field input, .wh-field select, .wh-field textarea { padding:10px 12px; border:1px solid rgba(255,255,255,0.08); border-radius:10px; font-size:14px; outline:none; transition:border-color 0.2s; background:rgba(26,26,36,0.8); color:#FFFFFF; }
        .wh-field input:focus, .wh-field select:focus, .wh-field textarea:focus { border-color:#3B82F6; box-shadow:0 0 0 3px rgba(59,130,246,0.1); }
        .wh-field select option { background:#111118; }
        .wh-detail-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
        .wh-detail-item { background:rgba(26,26,36,0.6); border:1px solid rgba(255,255,255,0.05); border-radius:10px; padding:12px 16px; }
        .wh-detail-item span { font-size:12px; color:#6B7280; font-weight:500; display:block; margin-bottom:4px; }
        .wh-detail-item strong { font-size:15px; color:#FFFFFF; }
        .wh-detail-full { grid-column:1/-1; }
      `}</style>
    </div>
  );
}
